import React, { FC, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import DownloadIcon from '../../../../assets/icons/DownloadIcon';
import LinkedInIcon from '../../../../assets/icons/LinkedInIcon';
import ShareIcon from '../../../../assets/icons/ShareIcon';
import Modal, { Modals } from '../../../../modals/Modal';
import { generateImageKitUrl } from '../../../../modules/imageKit';
import styled from '../../../../styled';
import { colorsPalette } from '../../../../theme';
import { generateCertificateName, getCertificatePDFUrl } from '../../../../utils/helpers';
import useDownloadFile from '../../../../utils/hooks/useDownloadFile';
import { LightTooltip } from '../../../UI/LightTooltip';
import useStyles from './styles';

interface CardTestResultsProps {
  hash?: string | null;
  passDate?: string | null;
  pathwayName?: string | null;
  tenantTitle?: string | null;
  tenantName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
}

const CardTestResults: FC<CardTestResultsProps> = ({
  hash,
  passDate,
  pathwayName,
  tenantTitle,
  tenantName,
  cpdCredits,
  category,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { download, loading: loadingDownloadCertificate } = useDownloadFile();

  const certName = generateCertificateName(tenantTitle, pathwayName);
  const certUrl = useMemo(
    () =>
      generateImageKitUrl({
        path: getCertificatePDFUrl(hash, tenantName),
        transformation: undefined,
      }),
    [hash, tenantName],
  );

  return (
    <div data-testid="cardTestResults" className={classes.wrapper}>
      <div className={classes.btnWrapper}>
        <Modal entityName={pathwayName} passDate={passDate} resultHash={hash}>
          {({ openModal }) => (
            <LightTooltip
              classes={{
                tooltip: classes.customTooltip,
                popper: classes.customTooltipPopper,
              }}
              title={t('page.pathway.addToProfile') || ''}
              placement="top"
            >
              <button
                data-testid="addToProfileBtn"
                className={classes.btn}
                onClick={() => openModal(Modals.LinkedInModal)}
              >
                <LinkedInIcon color={colorsPalette.white} className={classes.icon} />
                <span className={classes.btnText}>{t('page.pathway.addToProfile')}</span>
              </button>
            </LightTooltip>
          )}
        </Modal>
      </div>
      <div className={classes.btnWrapper}>
        <LightTooltip
          classes={{
            tooltip: classes.customTooltip,
            popper: classes.customTooltipPopper,
          }}
          title={t('page.pathway.cpdCertificate') || ''}
          placement="top"
        >
          <DownloadButton
            data-testid="downloadCertificateBtn"
            className={classes.btn}
            onClick={() => !loadingDownloadCertificate && download(certName, certUrl)}
            disabled={loadingDownloadCertificate}
          >
            <DownloadIcon color={colorsPalette.white} className={classes.icon} />
            <span className={classes.btnText}>{t('page.pathway.cpdCertificate')}</span>
          </DownloadButton>
        </LightTooltip>
      </div>
      <div className={classes.btnWrapper}>
        <Modal
          resultHash={hash}
          pathwayName={pathwayName}
          cpdCredits={cpdCredits}
          category={category}
        >
          {({ openModal }) => (
            <LightTooltip
              classes={{
                tooltip: classes.customTooltip,
                popper: classes.customTooltipPopper,
              }}
              title={t('page.pathway.share') || ''}
              placement="top"
            >
              <button
                data-testid="openShareModalBtn"
                className={classes.btn}
                onClick={() => openModal(Modals.ShareModal)}
              >
                <ShareIcon color={colorsPalette.white} className={classes.icon} />
                <span className={classes.btnText}>{t('page.pathway.share')}</span>
              </button>
            </LightTooltip>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default CardTestResults;

const DownloadButton = styled('button')`
  transition: opacity 0.3s;
  opacity: ${({ disabled }) => disabled && 0.3};
  pointer-events: ${({ disabled }) => disabled && 'none'};
`;
